import React, { createContext, useContext, useState, ReactNode } from 'react';

interface AudioUIContextType {
  hoveredTimeRange: { start: number; end?: number } | null;
  setHoveredTimeRange: (range: { start: number; end?: number } | null) => void;
  playingWidgetId: string | null;
  setPlayingWidgetId: (id: string | null) => void;
  // Drag highlight time for transcript word highlighting during timeline drag operations
  dragHighlightTime: number | null;
  setDragHighlightTime: (time: number | null) => void;
  cursorSyncEnabled: boolean;
  setCursorSyncEnabled: (enabled: boolean) => void;
  syncRequestId: number;
  syncRequestTargetTime: number | null;
  requestCursorSync: (time?: number | null) => void;
}

const AudioUIContext = createContext<AudioUIContextType | undefined>(undefined);

export function AudioUIProvider({ children }: { children: ReactNode }) {
  const [hoveredTimeRange, setHoveredTimeRange] = useState<{ start: number; end?: number } | null>(null);
  const [playingWidgetId, setPlayingWidgetId] = useState<string | null>(null);
  const [dragHighlightTime, setDragHighlightTime] = useState<number | null>(null);
  const [cursorSyncEnabled, setCursorSyncEnabled] = useState(true);
  const [syncRequestId, setSyncRequestId] = useState(0);
  const [syncRequestTargetTime, setSyncRequestTargetTime] = useState<number | null>(null);

  const requestCursorSync = (time?: number | null) => {
    setCursorSyncEnabled(true);
    setSyncRequestTargetTime(Number.isFinite(time as number) ? Number(time) : null);
    setSyncRequestId((previous) => previous + 1);
  };

  return (
    <AudioUIContext.Provider value={{
      hoveredTimeRange,
      setHoveredTimeRange,
      playingWidgetId,
      setPlayingWidgetId,
      dragHighlightTime,
      setDragHighlightTime,
      cursorSyncEnabled,
      setCursorSyncEnabled,
      syncRequestId,
      syncRequestTargetTime,
      requestCursorSync,
    }}>
      {children}
    </AudioUIContext.Provider>
  );
}

export function useAudioUI() {
  const context = useContext(AudioUIContext);
  if (!context) {
    throw new Error('useAudioUI must be used within AudioUIProvider');
  }
  return context;
}
